import { bookingCleaners } from "@/mocks/booking";
import type { BookingState } from "@/hooks/useBooking";

type Props = {
  state: BookingState;
  update: <K extends keyof BookingState>(k: K, v: BookingState[K]) => void;
};

export default function StepCleaner({ state, update }: Props) {
  // mark cleaners as available for the chosen time
  const available = bookingCleaners.map((c) => ({
    ...c,
    canDoTime: state.time ? c.available.includes(state.time) : true,
  }));

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-heading text-foreground-950 text-3xl md:text-4xl font-medium mb-2">
          Pick your cleaner.
        </h2>
        <p className="text-foreground-700 text-sm md:text-base">
          Choose someone you click with, or let us auto-match you with the closest available pro.
        </p>
      </div>

      <button
        type="button"
        onClick={() => update("cleanerId", null)}
        className={`w-full text-left rounded-2xl px-5 py-4 border-2 transition-colors flex items-center justify-between ${
          state.cleanerId === null
            ? "border-primary-500 bg-primary-50"
            : "border-background-200 bg-background-50 hover:border-foreground-300"
        }`}
      >
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 flex items-center justify-center rounded-full bg-foreground-950 text-background-50">
            <i className="ri-magic-line text-lg"></i>
          </span>
          <div>
            <p className="font-heading text-foreground-950 text-lg font-medium">Auto-match me</p>
            <p className="text-foreground-700 text-sm">We'll pair you with the top-rated cleaner available.</p>
          </div>
        </div>
        <span className="text-xs text-primary-700 font-medium hidden md:inline">Recommended</span>
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        {available.map((c) => {
          const active = state.cleanerId === c.id;
          return (
            <article
              key={c.id}
              className={`relative rounded-2xl overflow-hidden border-2 transition-colors flex flex-col sm:flex-row ${
                active
                  ? "border-primary-500 bg-primary-50"
                  : "border-background-200 bg-background-50"
              } ${!c.canDoTime ? "opacity-60" : ""}`}
            >
              <div className="sm:w-40 h-48 sm:h-auto flex-shrink-0">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1 p-5">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div>
                    <h3 className="font-heading text-foreground-950 text-lg font-medium leading-tight">
                      {c.name}
                    </h3>
                    <p className="text-foreground-600 text-xs">{c.role}</p>
                  </div>
                  <span className="text-right">
                    <p className="font-heading text-foreground-950 text-lg font-medium leading-none">
                      ${c.rate}
                    </p>
                    <p className="text-[10px] text-foreground-600">per hr</p>
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-2 mb-3 text-xs text-foreground-700">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-star-fill text-secondary-500"></i>
                    {c.rating}
                  </span>
                  <span className="text-foreground-400">·</span>
                  <span>{c.jobs} jobs</span>
                </div>
                <p className="text-foreground-700 text-sm leading-snug mb-3">{c.bio}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {c.highlights.map((h) => (
                    <span
                      key={h}
                      className="text-[11px] bg-secondary-100 text-secondary-900 px-2 py-1 rounded-full whitespace-nowrap"
                    >
                      {h}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  disabled={!c.canDoTime}
                  onClick={() => update("cleanerId", c.id)}
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-full text-sm font-medium py-2.5 whitespace-nowrap transition-colors ${
                    active
                      ? "bg-primary-500 text-background-50"
                      : c.canDoTime
                        ? "bg-foreground-950 text-background-50 hover:bg-foreground-900"
                        : "bg-background-200 text-foreground-500 cursor-not-allowed"
                  }`}
                >
                  {active ? (
                    <>
                      <i className="ri-check-line"></i>Selected
                    </>
                  ) : c.canDoTime ? (
                    "Choose this cleaner"
                  ) : (
                    "Unavailable at this time"
                  )}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}